const BODY_LOCK_KEY = 'imageCompareScrollLockCount';
const BODY_OVERFLOW_KEY = 'imageCompareScrollLockOverflow';
const BODY_PADDING_KEY = 'imageCompareScrollLockPaddingRight';

function lockBodyScroll() {
  if (typeof document === 'undefined' || !document.body) {
    return;
  }

  const body = document.body;
  const count = Number(body.dataset[BODY_LOCK_KEY] || 0);

  if (count === 0) {
    body.dataset[BODY_OVERFLOW_KEY] = body.style.overflow || '';
    body.dataset[BODY_PADDING_KEY] = body.style.paddingRight || '';

    const scrollbarGap = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = 'hidden';

    if (scrollbarGap > 0) {
      body.style.paddingRight = `${scrollbarGap}px`;
    }
  }

  body.dataset[BODY_LOCK_KEY] = String(count + 1);
}

function unlockBodyScroll() {
  if (typeof document === 'undefined' || !document.body) {
    return;
  }

  const body = document.body;
  const count = Number(body.dataset[BODY_LOCK_KEY] || 0);

  if (count <= 1) {
    body.style.overflow = body.dataset[BODY_OVERFLOW_KEY] || '';
    body.style.paddingRight = body.dataset[BODY_PADDING_KEY] || '';
    delete body.dataset[BODY_LOCK_KEY];
    delete body.dataset[BODY_OVERFLOW_KEY];
    delete body.dataset[BODY_PADDING_KEY];
    return;
  }

  body.dataset[BODY_LOCK_KEY] = String(count - 1);
}

class ImageCompare {
  constructor(el, settings = {}) {
    const defaults = {
      controlColor: '#FFFFFF',
      controlShadow: true,
      addCircle: false,
      addCircleBlur: true,
      showLabels: false,
      labelOptions: {
        before: 'Before',
        after: 'After',
        onHover: false,
      },
      smoothing: true,
      smoothingAmount: 100,
      hoverStart: false,
      verticalMode: false,
      startingPoint: 50,
      fluidMode: false,
    };

    this.settings = Object.assign(defaults, settings);
    this.animationFrameId = null;
    this.safariAgent =
      navigator.userAgent.indexOf('Safari') !== -1 &&
      navigator.userAgent.indexOf('Chrome') === -1;
    this.el = el;
    this.wrapper = null;
    this.control = null;
    this.arrowContainer = null;
    this.arrowAnimator = [];
    this.active = false;
    this.slideWidth = 50;
    this.lineWidth = 2;
    this.arrowCoordinates = {
      circle: [5, 3],
      standard: [8, 0],
    };
  }

  mount() {
    if (this.safariAgent) {
      this.settings.smoothing = false;
    }

    this._shapeContainer();
    this._getImages();
    this._buildControl();
    this._events();
  }

  _events() {
    this.el.addEventListener('mousedown', (event) => {
      this._activate(true);
      document.body.classList.add('icv__body');
      lockBodyScroll();
      this._slideCompare(event);
    });

    this.el.addEventListener('mousemove', (event) => {
      if (this.active) {
        this._slideCompare(event);
      }
    });

    this.el.addEventListener('mouseup', () => this._activate(false));

    document.body.addEventListener('mouseup', () => {
      document.body.classList.remove('icv__body');
      unlockBodyScroll();
      this._activate(false);
    });

    this.control.addEventListener('touchstart', () => {
      this._activate(true);
      document.body.classList.add('icv__body');
      lockBodyScroll();
    });

    this.el.addEventListener('touchmove', (event) => {
      if (this.active) {
        this._slideCompare(event);
      }
    });

    this.el.addEventListener('touchend', () => {
      this._activate(false);
      document.body.classList.remove('icv__body');
      unlockBodyScroll();
    });

    this.el.addEventListener('mouseenter', () => {
      if (this.settings.hoverStart) {
        this._activate(true);
      }

      const coord = this.settings.addCircle
        ? this.arrowCoordinates.circle
        : this.arrowCoordinates.standard;

      this.arrowAnimator.forEach((animator, index) => {
        animator.style.cssText = this.settings.verticalMode
          ? `transform: translateY(${coord[1] * (index === 0 ? 1 : -1)}px);`
          : `transform: translateX(${coord[1] * (index === 0 ? 1 : -1)}px);`;
      });
    });

    this.el.addEventListener('mouseleave', () => {
      const coord = this.settings.addCircle
        ? this.arrowCoordinates.circle
        : this.arrowCoordinates.standard;

      this.arrowAnimator.forEach((animator, index) => {
        animator.style.cssText = this.settings.verticalMode
          ? `transform: translateY(${index === 0 ? `${coord[0]}px` : `-${coord[0]}px`});`
          : `transform: translateX(${index === 0 ? `${coord[0]}px` : `-${coord[0]}px`});`;
      });
    });
  }

  _slideCompare(event) {
    if (this.animationFrameId !== null) {
      return;
    }

    this.animationFrameId = requestAnimationFrame(() => {
      const bounds = this.el.getBoundingClientRect();
      const x =
        event.touches !== undefined
          ? event.touches[0].clientX - bounds.left
          : event.clientX - bounds.left;
      const y =
        event.touches !== undefined
          ? event.touches[0].clientY - bounds.top
          : event.clientY - bounds.top;
      const position = this.settings.verticalMode
        ? (y / bounds.height) * 100
        : (x / bounds.width) * 100;

      if (position >= 0 && position <= 100) {
        if (this.settings.verticalMode) {
          this.control.style.top = `calc(${position}% - ${this.slideWidth / 2}px)`;
        } else {
          this.control.style.left = `calc(${position}% - ${this.slideWidth / 2}px)`;
        }

        if (this.settings.fluidMode) {
          this.wrapper.style.clipPath = this.settings.verticalMode
            ? `inset(0 0 ${100 - position}% 0)`
            : `inset(0 0 0 ${position}%)`;
        } else if (this.settings.verticalMode) {
          this.wrapper.style.height = `calc(${position}%)`;
        } else {
          this.wrapper.style.width = `calc(${100 - position}%)`;
        }
      }

      this.animationFrameId = null;
    });
  }

  _activate(state) {
    this.active = state;
  }

  _shapeContainer() {
    const imposter = document.createElement('div');
    const labelBefore = document.createElement('span');
    const labelAfter = document.createElement('span');

    labelBefore.classList.add('icv__label', 'icv__label-before', 'keep');
    labelAfter.classList.add('icv__label', 'icv__label-after', 'keep');

    if (this.settings.labelOptions.onHover) {
      labelBefore.classList.add('on-hover');
      labelAfter.classList.add('on-hover');
    }

    if (this.settings.verticalMode) {
      labelBefore.classList.add('vertical');
      labelAfter.classList.add('vertical');
    }

    labelBefore.textContent = this.settings.labelOptions.before || 'Before';
    labelAfter.textContent = this.settings.labelOptions.after || 'After';

    if (this.settings.showLabels) {
      this.el.appendChild(labelBefore);
      this.el.appendChild(labelAfter);
    }

    this.el.classList.add(
      'icv',
      this.settings.verticalMode ? 'icv__icv--vertical' : 'icv__icv--horizontal',
      this.settings.fluidMode ? 'icv__is--fluid' : 'standard',
    );

    imposter.classList.add('icv__imposter');
    this.el.appendChild(imposter);
  }

  _buildControl() {
    const control = document.createElement('div');
    const lineStart = document.createElement('div');
    const arrows = document.createElement('div');
    const circle = document.createElement('div');
    const arrowSize = '20';

    arrows.classList.add('icv__theme-wrapper');

    for (let index = 0; index <= 1; index += 1) {
      const animator = document.createElement('div');
      const arrow = `<svg
      height="15"
      width="15"
      style="
      transform:
      scale(${this.settings.addCircle ? 0.7 : 1.5})
      rotateZ(${
        index === 0
          ? this.settings.verticalMode
            ? '-90deg'
            : '180deg'
          : this.settings.verticalMode
            ? '90deg'
            : '0deg'
      });
      height: ${arrowSize}px;
      width: ${arrowSize}px;
      ${
        this.settings.controlShadow
          ? `
      -webkit-filter: drop-shadow(0px 3px 5px rgba(0, 0, 0, .33));
      filter: drop-shadow(0px ${index === 0 ? '-3px' : '3px'} 5px rgba(0, 0, 0, .33));
      `
          : ''
      }
      "
      xmlns="http://www.w3.org/2000/svg" data-name="Layer 1" viewBox="0 0 15 15">
      <path
      ${this.settings.addCircle ? 'fill="transparent"' : `fill="${this.settings.controlColor}"`}
      stroke="${this.settings.controlColor}"
      stroke-linecap="round"
      stroke-width="${this.settings.addCircle ? 3 : 0}"
      d="M4.5 1.9L10 7.65l-5.5 5.4"
      />
    </svg>`;

      animator.innerHTML += arrow;
      this.arrowAnimator.push(animator);
      arrows.appendChild(animator);
    }

    const coord = this.settings.addCircle
      ? this.arrowCoordinates.circle
      : this.arrowCoordinates.standard;

    this.arrowAnimator.forEach((animator, index) => {
      animator.classList.add('icv__arrow-wrapper');
      animator.style.cssText = this.settings.verticalMode
        ? `transform: translateY(${index === 0 ? `${coord[0]}px` : `-${coord[0]}px`});`
        : `transform: translateX(${index === 0 ? `${coord[0]}px` : `-${coord[0]}px`});`;
    });

    control.classList.add('icv__control');
    control.style.cssText = `
      ${this.settings.verticalMode ? 'height' : 'width'}: ${this.slideWidth}px;
      ${this.settings.verticalMode ? 'top' : 'left'}: calc(${this.settings.startingPoint}% - ${this.slideWidth / 2}px);
      ${
        'ontouchstart' in document.documentElement
          ? ''
          : this.settings.smoothing
            ? `transition: ${this.settings.smoothingAmount}ms ease-out;`
            : ''
      }
    `;

    lineStart.classList.add('icv__control-line');
    lineStart.style.cssText = `
      ${this.settings.verticalMode ? 'height' : 'width'}: ${this.lineWidth}px;
      background: ${this.settings.controlColor};
      ${this.settings.controlShadow ? 'box-shadow: 0px 0px 15px rgba(0,0,0,0.33);' : ''}
    `;

    const lineEnd = lineStart.cloneNode(true);

    circle.classList.add('icv__circle');
    circle.style.cssText = `
      ${this.settings.addCircleBlur ? '-webkit-backdrop-filter: blur(5px); backdrop-filter: blur(5px);' : ''}
      border: ${this.lineWidth}px solid ${this.settings.controlColor};
      ${this.settings.controlShadow ? 'box-shadow: 0px 0px 15px rgba(0,0,0,0.33);' : ''}
    `;

    control.appendChild(lineStart);

    if (this.settings.addCircle) {
      control.appendChild(circle);
    }

    control.appendChild(arrows);
    control.appendChild(lineEnd);

    this.arrowContainer = arrows;
    this.control = control;
    this.el.appendChild(control);
  }

  _getImages() {
    const children = this.el.querySelectorAll('img, video, .keep');
    this.el.innerHTML = '';

    children.forEach((child) => {
      this.el.appendChild(child);
    });

    const media = [...children].filter((element) =>
      ['img', 'video'].includes(element.nodeName.toLowerCase()),
    );

    if (this.settings.verticalMode) {
      media.reverse();
    }

    for (let index = 0; index <= 1; index += 1) {
      const child = media[index];

      if (!child) {
        continue;
      }

      child.classList.add('icv__img');
      child.classList.add(index === 0 ? 'icv__img-a' : 'icv__img-b');

      if (index === 1) {
        const wrapper = document.createElement('div');
        const afterUrl = media[1].src;

        wrapper.classList.add('icv__wrapper');
        wrapper.style.cssText = `
          width: ${100 - this.settings.startingPoint}%;
          height: ${this.settings.startingPoint}%;
          ${
            'ontouchstart' in document.documentElement
              ? ''
              : this.settings.smoothing
                ? `transition: ${this.settings.smoothingAmount}ms ease-out;`
                : ''
          }
          ${
            this.settings.fluidMode
              ? `background-image: url(${afterUrl}); clip-path: inset(${
                  this.settings.verticalMode
                    ? `0 0 ${100 - this.settings.startingPoint}% 0`
                    : `0 0 0 ${this.settings.startingPoint}%`
                });`
              : ''
          }
        `;

        wrapper.appendChild(child);
        this.wrapper = wrapper;
        this.el.appendChild(this.wrapper);
      }
    }

    if (this.settings.fluidMode && media[0]) {
      const url = media[0].src;
      const fluidWrapper = document.createElement('div');

      fluidWrapper.classList.add('icv__fluidwrapper');
      fluidWrapper.style.cssText = `background-image: url(${url});`;
      this.el.appendChild(fluidWrapper);
    }
  }
}

export default ImageCompare;
